import { CwjTaskEngine } from "../cwj-task-engine/index.js";
import { CwjWorkflowEngine } from "../cwj-workflow/index.js";
import { getStageDefinition } from "../cwj-workflow/constants.js";
import { generateRequestId } from "../audit/requestId.js";
import { CWJ_WORKSPACE_VIEWS, DEFAULT_STORAGE_KEY, } from "./constants.js";
import { CwjWorkspaceError } from "./types.js";
import { MemoryCwjWorkspaceMutationCoordinator } from "./mutationCoordinator.js";
import { CwjWorkspaceTransactionStore } from "./transactionStore.js";
function isMutableTaskStatus(status) {
    return status !== "completed" && status !== "failed" && status !== "cancelled" && status !== "expired";
}
function isMutableWorkflowStatus(status) {
    return status !== "completed" && status !== "failed" && status !== "cancelled";
}
export class CwjWorkspace {
    constructor(options) {
        this.tasks = [];
        this.view = "inbox";
        this.emergencyStopScopes = [];
        this.theme = "dark";
        this.locale = "en";
        if (!options.projectId || !options.skillId) {
            throw new CwjWorkspaceError("missing_project_context: projectId and skillId are required", "missing_project_context");
        }
        this.ownerId = options.ownerId;
        this.tenantId = options.tenantId;
        this.defaultProjectId = options.projectId;
        this.defaultSkillId = options.skillId;
        const clock = options.clock ?? (() => Date.now());
        this.generateId = options.generateId ?? (() => `task-${generateRequestId()}`);
        const txStore = new CwjWorkspaceTransactionStore();
        const taskStore = txStore.asTaskStore();
        const workflowStore = txStore.asWorkflowStore();
        this.taskEngine = new CwjTaskEngine({
            store: taskStore,
            clock,
            generateTaskId: this.generateId,
        });
        this.workflowEngine = new CwjWorkflowEngine({
            store: workflowStore,
            taskEngine: this.taskEngine,
            clock,
            generateWorkflowId: this.generateId,
        });
        this.coordinator = new MemoryCwjWorkspaceMutationCoordinator(this.taskEngine, this.workflowEngine, txStore);
        this.storage = options.storage;
        this.storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
    }
    assertOwner(actor) {
        if (!actor || actor.authenticated !== true) {
            throw new CwjWorkspaceError("actor_unauthenticated: an authenticated actor is required", "actor_unauthenticated");
        }
        if (this.tenantId !== undefined && actor.tenantId !== this.tenantId) {
            throw new CwjWorkspaceError(`actor_tenant_mismatch: actor tenant ${actor.tenantId ?? "<unset>"} does not match workspace tenant ${this.tenantId}`, "actor_tenant_mismatch");
        }
        if (actor.actorId !== this.ownerId) {
            throw new CwjWorkspaceError(`actor_unauthorized: actor ${actor.actorId} is not the workspace owner`, "actor_unauthorized");
        }
        if (!actor.roles.includes("owner")) {
            throw new CwjWorkspaceError(`actor_unauthorized: actor ${actor.actorId} lacks owner role`, "actor_unauthorized");
        }
    }
    projectId(input) {
        return input ?? this.defaultProjectId;
    }
    skillId(input) {
        return input ?? this.defaultSkillId;
    }
    get owner() {
        return this.ownerId;
    }
    get currentView() {
        return this.view;
    }
    get currentTheme() {
        return this.theme;
    }
    get currentLocale() {
        return this.locale;
    }
    get selectedTaskId() {
        return this.currentTaskId;
    }
    getEmergencyStopScopes() {
        return [...this.emergencyStopScopes];
    }
    async load() {
        if (!this.storage)
            return;
        const raw = await this.storage.getItem(this.storageKey);
        if (!raw)
            return;
        let snapshot;
        try {
            snapshot = JSON.parse(raw);
        }
        catch {
            // corrupted snapshot: overwrite with a clean metadata-only snapshot
            await this.persist();
            return;
        }
        if (snapshot.version !== 2) {
            await this.persist();
            return;
        }
        if (typeof snapshot.selectedTaskId !== "string" &&
            snapshot.selectedTaskId !== undefined &&
            snapshot.selectedTaskId !== null) {
            await this.persist();
            return;
        }
        this.currentTaskId = snapshot.selectedTaskId ?? undefined;
        this.view = CWJ_WORKSPACE_VIEWS.includes(snapshot.view) ? snapshot.view : "inbox";
        this.theme = snapshot.theme === "light" ? "light" : "dark";
        this.locale = snapshot.locale === "hi" ? "hi" : "en";
        await this.refreshAll();
    }
    async persist() {
        if (!this.storage)
            return;
        const snapshot = {
            version: 2,
            selectedTaskId: this.currentTaskId,
            view: this.view,
            theme: this.theme,
            locale: this.locale,
        };
        await this.storage.setItem(this.storageKey, JSON.stringify(snapshot));
    }
    setView(view) {
        this.view = view;
    }
    setTheme(theme) {
        this.theme = theme;
    }
    setLocale(locale) {
        this.locale = locale;
    }
    makeTaskContext(actor, projectId, skillId, channel) {
        return {
            tenantId: this.tenantId,
            ownerId: this.ownerId,
            projectId,
            skillId,
            requestedBy: actor.actorId,
            channel: channel,
            sessionId: undefined,
        };
    }
    makeWorkflowContext(actor, projectId, skillId, channel) {
        return {
            tenantId: this.tenantId,
            ownerId: this.ownerId,
            projectId,
            skillId,
            requestedBy: actor.actorId,
            channel: channel,
        };
    }
    async createTaskFromChat(input, actor) {
        this.assertOwner(actor);
        const projectId = this.projectId(input.projectId);
        const skillId = this.skillId(input.skillId);
        const idempotencyKey = input.idempotencyKey;
        const taskInput = {
            title: input.title,
            description: input.description,
            payload: input.payload ?? { channel: "chat" },
            priority: "medium",
            requiresApproval: input.requiresApproval ?? false,
            idempotencyKey,
            maxDurationMs: undefined,
            expiresAt: undefined,
            taskId: this.generateId(),
        };
        const workflowInput = {
            title: input.title,
            description: input.description,
            payload: input.payload ?? { channel: "chat" },
            idempotencyKey,
            maxDurationMs: undefined,
        };
        const result = await this.coordinator.mutateTaskAndWorkflow({
            type: "create",
            actor,
            taskContext: this.makeTaskContext(actor, projectId, skillId, "chat"),
            taskInput,
            workflowContext: this.makeWorkflowContext(actor, projectId, skillId, "chat"),
            workflowInput,
        }, 0, 0);
        const summary = this.buildSummary(result.task, result.workflow);
        this.tasks.unshift(summary);
        this.currentTaskId = summary.taskId;
        await this.persist();
        return summary;
    }
    async openTask(taskId) {
        const cached = this.tasks.find((t) => t.taskId === taskId);
        const task = await this.taskEngine.getTask(taskId);
        const workflow = await this.workflowEngine.getWorkflow(taskId);
        if (!task && !workflow && !cached)
            return undefined;
        let summary;
        if (task && workflow) {
            summary = this.buildSummary(task, workflow);
        }
        else if (cached) {
            summary = cached;
        }
        else if (task) {
            summary = this.buildSummaryFromTask(task);
        }
        if (summary)
            this.currentTaskId = summary.taskId;
        return summary;
    }
    async openTaskForCode(taskId, actor) {
        this.assertOwner(actor);
        return this.openTask(taskId);
    }
    async openTaskForCowork(taskId, actor) {
        this.assertOwner(actor);
        return this.openTask(taskId);
    }
    async linkVoiceTranscript(input, actor) {
        this.assertOwner(actor);
        const taskId = input.taskId ?? this.currentTaskId;
        if (!taskId)
            return undefined;
        const task = await this.taskEngine.getTask(taskId);
        if (!task)
            return undefined;
        const workflow = (await this.workflowEngine.getWorkflow(taskId)) ?? undefined;
        const updates = {
            payload: {
                ...(task.input.payload ?? {}),
                voiceTranscriptPreview: input.text.slice(0, 200),
            },
        };
        const result = await this.coordinator.mutateTaskAndWorkflow({
            type: "update",
            actor,
            taskId,
            updates,
        }, task.version, workflow?.version ?? 0);
        const summary = this.refreshSummary(result.task, workflow, taskId);
        await this.persist();
        return summary;
    }
    async pauseTask(taskId, actor) {
        this.assertOwner(actor);
        const task = await this.taskEngine.getTask(taskId);
        const workflow = await this.workflowEngine.getWorkflow(taskId);
        if (!task || !workflow)
            return undefined;
        const result = await this.coordinator.mutateTaskAndWorkflow({ type: "pause", actor, taskId }, task.version, workflow.version);
        const summary = this.refreshSummary(result.task, result.workflow, taskId);
        await this.persist();
        return summary;
    }
    async resumeTask(taskId, actor) {
        this.assertOwner(actor);
        const task = await this.taskEngine.getTask(taskId);
        const workflow = await this.workflowEngine.getWorkflow(taskId);
        if (!task || !workflow)
            return undefined;
        const result = await this.coordinator.mutateTaskAndWorkflow({ type: "resume", actor, taskId }, task.version, workflow.version);
        const summary = this.refreshSummary(result.task, result.workflow, taskId);
        await this.persist();
        return summary;
    }
    async cancelTask(taskId, actor) {
        this.assertOwner(actor);
        const task = await this.taskEngine.getTask(taskId);
        const workflow = await this.workflowEngine.getWorkflow(taskId);
        if (!task || !workflow)
            return undefined;
        const result = await this.coordinator.mutateTaskAndWorkflow({ type: "cancel", actor, taskId }, task.version, workflow.version);
        const summary = this.refreshSummary(result.task, result.workflow, taskId);
        await this.persist();
        return summary;
    }
    async approveStage(taskId, actor) {
        this.assertOwner(actor);
        const task = await this.taskEngine.getTask(taskId);
        const workflow = await this.workflowEngine.getWorkflow(taskId);
        if (!task || !workflow)
            return undefined;
        const result = await this.coordinator.mutateTaskAndWorkflow({ type: "approve", actor, taskId }, task.version, workflow.version);
        const summary = this.refreshSummary(result.task, result.workflow, taskId);
        await this.persist();
        return summary;
    }
    setEmergencyStop(scope, active, actor, reason) {
        this.assertOwner(actor);
        this.taskEngine.setEmergencyStop(scope, active, reason);
        this.workflowEngine.setEmergencyStop(scope, active, reason);
        if (active) {
            if (!this.emergencyStopScopes.includes(scope))
                this.emergencyStopScopes.push(scope);
        }
        else {
            this.emergencyStopScopes = this.emergencyStopScopes.filter((s) => s !== scope);
        }
        void this.persist();
    }
    isEmergencyStopped(scope) {
        const target = scope ?? "global";
        return this.taskEngine.isEmergencyStopped(target) || this.workflowEngine.isEmergencyStopped(target);
    }
    listTasks(view) {
        const target = view ?? this.view;
        if (target === "all")
            return [...this.tasks];
        return this.tasks.filter((t) => t.view === target);
    }
    async refreshAll() {
        const taskFilters = {
            ownerId: this.ownerId,
            projectId: this.defaultProjectId,
            skillId: this.defaultSkillId,
        };
        if (this.tenantId)
            taskFilters.tenantId = this.tenantId;
        const tasks = await this.taskEngine.listTasks(taskFilters);
        const workflowFilters = {
            ownerId: this.ownerId,
            projectId: this.defaultProjectId,
            skillId: this.defaultSkillId,
        };
        if (this.tenantId)
            workflowFilters.tenantId = this.tenantId;
        const workflows = await this.workflowEngine.listWorkflows(workflowFilters);
        const workflowById = new Map(workflows.map((w) => [w.workflowId, w]));
        const updated = [];
        for (const task of tasks) {
            const workflow = workflowById.get(task.taskId);
            if (workflow) {
                updated.push(this.buildSummary(task, workflow));
            }
            else {
                updated.push(this.buildSummaryFromTask(task));
            }
        }
        this.tasks = updated;
        await this.persist();
    }
    refreshSummary(task, workflow, taskId) {
        const index = this.tasks.findIndex((t) => t.taskId === taskId);
        if (task && workflow) {
            const summary = this.buildSummary(task, workflow);
            if (index >= 0)
                this.tasks[index] = summary;
            else
                this.tasks.unshift(summary);
            return summary;
        }
        if (index >= 0)
            return this.tasks[index];
        return undefined;
    }
    buildSummary(task, workflow) {
        const stage = getStageDefinition(workflow.currentStageId);
        const stageStatus = workflow.stageStates[workflow.currentStageId]?.status ?? "pending";
        const view = this.deriveView(task.status, workflow.status, workflow.currentStageId, stageStatus);
        const evidenceCount = Object.values(workflow.stageStates).reduce((sum, s) => sum + s.evidence.filter((e) => !e.superseded).length, 0);
        const payload = task.input.payload;
        const voiceTranscriptPreview = typeof payload?.voiceTranscriptPreview === "string" ? payload.voiceTranscriptPreview : undefined;
        return {
            taskId: task.taskId,
            title: task.input.title,
            description: task.input.description,
            taskStatus: task.status,
            workflowStatus: workflow.status,
            currentStageId: workflow.currentStageId,
            currentStageLabel: stage?.title ?? workflow.currentStageId,
            view,
            channel: task.context.channel,
            projectId: task.context.projectId,
            skillId: task.context.skillId,
            ownerId: task.context.ownerId ?? this.ownerId,
            tenantId: task.context.tenantId,
            updatedAt: task.updatedAt,
            createdAt: task.createdAt,
            isPaused: task.status === "paused" || workflow.status === "paused",
            canCancel: isMutableTaskStatus(task.status) && isMutableWorkflowStatus(workflow.status),
            canResume: task.status === "paused" || workflow.status === "paused",
            evidenceCount,
            voiceTranscriptPreview,
        };
    }
    buildSummaryFromTask(partial) {
        const cached = partial;
        if ("view" in cached)
            return cached;
        const task = partial;
        return {
            taskId: task.taskId,
            title: task.input.title,
            description: task.input.description,
            taskStatus: task.status,
            workflowStatus: "pending",
            currentStageId: "intake",
            currentStageLabel: "Intake",
            view: this.deriveView(task.status, "pending", "intake", "pending"),
            channel: task.context.channel,
            projectId: task.context.projectId,
            skillId: task.context.skillId,
            ownerId: task.context.ownerId ?? this.ownerId,
            tenantId: task.context.tenantId,
            updatedAt: task.updatedAt,
            createdAt: task.createdAt,
            isPaused: task.status === "paused",
            canCancel: isMutableTaskStatus(task.status),
            canResume: task.status === "paused",
            evidenceCount: 0,
            voiceTranscriptPreview: undefined,
        };
    }
    deriveView(taskStatus, workflowStatus, stageId, stageStatus) {
        if (taskStatus === "paused" || workflowStatus === "paused")
            return "paused";
        if (taskStatus === "approval_required")
            return "approval";
        if (stageId === "approval" || stageId === "owner_approval") {
            if (stageStatus !== "completed")
                return "approval";
        }
        if (stageId === "code_review" || stageId === "release_evidence")
            return "review";
        if (stageId === "qa")
            return "qa";
        if (taskStatus === "completed" || workflowStatus === "completed")
            return "completed";
        if (taskStatus === "failed" || workflowStatus === "failed")
            return "failed";
        if (taskStatus === "cancelled" || workflowStatus === "cancelled")
            return "failed";
        if (taskStatus === "running" || workflowStatus === "running")
            return "active";
        if (taskStatus === "pending")
            return "inbox";
        return "active";
    }
}
//# sourceMappingURL=workspace.js.map